import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'react-feather';
import { Menu, MenuSize } from '@/shared/components/Menu';
import { SUBMITTED_FORM_LABELS } from '@epics/taskFormManagement/constants/labels';
import type { SubmittedForm } from '@epics/taskFormManagement/types';

interface SubmittedFormCardProps {
  form: SubmittedForm;
  onView: (form: SubmittedForm) => void;
}

interface DropdownPosition {
  top: number;
  right: number;
}

function formatSubmissionDate(dateStr: string): string {
  if (!dateStr) {
    return '—';
  }
  // Take the calendar-date portion directly (handles "2026-06-23 05:37:00.857259+00"
  // and ISO strings) — avoids Date parsing of the non-standard "+00" offset and any
  // timezone day-shift.
  const datePart = dateStr.split(/[ T]/)[0] ?? '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (match) {
    return `${match[1]}/${match[2]}/${match[3]}`;
  }
  return dateStr;
}

export const SubmittedFormCard = ({ form, onView }: SubmittedFormCardProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition>({
    top: 0,
    right: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsMenuOpen(true);
  };

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };
    const handleScroll = () => setIsMenuOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isMenuOpen]);

  const formattedDate = formatSubmissionDate(form.submitted_at);

  return (
    <div className="bg-white border border-[#DCDDE0] rounded-[12px] p-6 flex flex-col gap-4 shadow-md">
      {/* Header: title + menu */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-base font-normal text-[18px] text-[#181A1B] leading-6 truncate">
          {form.task_form_name}
        </p>
        <button
          ref={buttonRef}
          type="button"
          aria-label="Actions"
          className="flex items-center justify-center rounded-full cursor-pointer hover:bg-[#474A4C]/[0.08] shrink-0"
          onClick={() => (isMenuOpen ? setIsMenuOpen(false) : openMenu())}
        >
          <MoreVertical size={20} className="text-on-surface-variant" />
        </button>
      </div>

      {/* Fields */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs text-[#474A4C]">
          <span className="font-normal">
            {SUBMITTED_FORM_LABELS.CARD_TASK_ID}:
          </span>
          <span className="text-[#181A1B]">{form.service_request_id}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#474A4C]">
          <span className="font-normal">
            {SUBMITTED_FORM_LABELS.CARD_FORM_ID}:
          </span>
          <span className="text-[#181A1B]">{form.generated_form_id}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#474A4C]">
          <span className="font-normal">
            {SUBMITTED_FORM_LABELS.CARD_SUBMISSION_DATE}:
          </span>
          <span className="text-[#181A1B]">{formattedDate}</span>
        </div>
      </div>

      {/* Portal menu */}
      {isMenuOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999]"
            style={{ top: dropdownPos.top, right: dropdownPos.right }}
          >
            <Menu
              items={[{ id: 'view', label: SUBMITTED_FORM_LABELS.ACTION_VIEW }]}
              size={MenuSize.Small}
              showLeadingIcons={false}
              showTrailingArrows={false}
              toolTip={false}
              onItemClick={() => {
                setIsMenuOpen(false);
                onView(form);
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
};
